/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createBrowserRouter } from "react-router-dom";

import { LocationUrls } from "../../model/navigation";
import { App } from "../App";
import AuthLayout from "./layouts/AuthLayout";
import MainLayout from "./layouts/MainLayout";
import NavLayout from "./layouts/NavLayout";
import RootErrorBoundary from "./RootErrorBoundary";
import { GuestRoute, ProtectedRoute } from "../guards";

/**
 * Application router created with createBrowserRouter.
 *
 * Route tree:
 *   /  — App (AppInitializer + NavigationInterceptor + Outlet)
 *     ├─ AuthLayout (pre-auth, no chrome)
 *     │   ├─ /          → SplashScreen  (lazy)
 *     │   └─ /signin    → SignIn        (lazy)
 *     └─ ProtectedRoute (auth guard)
 *         └─ MainLayout
 *             ├─ /player          → FullScreenPlayback      (lazy, no MenuStrip)
 *             └─ NavLayout (persistent MenuStrip in browser mode)
 *                 ├─ /select-profile  → SelectProfileScreen (lazy, branding only)
 *                 ├─ /home           → HomeScreen         (lazy)
 *                 ├─ /guide          → GuideScreen        (lazy)
 *                 ├─ /my-hub         → MyHubScreen        (lazy)
 *                 ├─ /my-hub/manage-recordings  → ManageSeriesRecordingsRoute (lazy)
 *                 ├─ /shows-movies   → ShowsAndMoviesScreen (lazy)
 *                 ├─ /content-detail → ContentDetailScreen (lazy)
 *                 ├─ /search → SearchScreen (lazy)
 *                 ├─ /see-more → SeeMoreScreen (lazy)
 *                 └─ /settings → SettingsScreen (lazy)
 *                 └─ /profiles → ManageProfilesScreen (lazy)
 *                 └─ /profile-details → ProfileDetailsScreen (lazy)
 *
 * Post-sign-in entry decision (in SignIn flow):
 *   - If account has multiple profiles: navigate to /select-profile.
 *   - Otherwise: navigate to /player on TV, or /home on browser.
 *
 * Screens are lazy-loaded via the react-router route-level `lazy` API.
 * All route paths reference LocationUrls constants (R1).
 *
 * A `lazy` import that fails — a chunk missing after a redeploy, a dropped
 * connection mid-download — surfaces to the root `errorElement`, which reloads
 * the app silently on the first failure and shows an error overlay with
 * Reload / Exit if it happens again. See {@link RootErrorBoundary}.
 */

// Load home screen
const loadHomeScreen = () => import("../HomeScreen/HomeScreen");

export const router = createBrowserRouter([
  {
    path: LocationUrls.SPLASH,
    element: <App />,
    errorElement: <RootErrorBoundary />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          {
            // Splash is the cold-start bootstrap screen and is intentionally
            // NOT wrapped in GuestRoute: it must always render on launch (to
            // show branding/loader) and run postSigninStartupSequence before
            // routing an authenticated user forward to the protected area.
            index: true,
            lazy: async () => {
              const { default: SplashScreen } =
                await import("../SplashScreen/SplashScreen");
              return { Component: SplashScreen };
            }
          },
          {
            // Sign-in is guarded: an already-authenticated user landing here
            // (back button, bookmark, typed URL) is redirected forward.
            element: <GuestRoute />,
            children: [
              {
                path: LocationUrls.SIGN_IN,
                lazy: async () => {
                  const { default: SignIn } = await import("../SignIn/SignIn");
                  return { Component: SignIn };
                }
              },
              {
                path: LocationUrls.FORGOT_PASSWORD,
                lazy: async () => {
                  const { default: RecoverPassword } =
                    await import("../SignIn/RecoverPassword/RecoverPassword");
                  return { Component: RecoverPassword };
                }
              },
              {
                path: LocationUrls.REQUEST_NEW_LINK,
                lazy: async () => {
                  const { default: RecoverPassword } =
                    await import("../SignIn/RecoverPassword/RecoverPassword");
                  return { Component: RecoverPassword };
                }
              },
              {
                path: LocationUrls.PASSWORD_HTML,
                lazy: async () => {
                  const { default: ResetPassword } =
                    await import("../SignIn/ResetPassword/ResetPassword");
                  return { Component: ResetPassword };
                }
              }
            ]
          }
        ]
      },
      {
        element: <ProtectedRoute />,
        children: [
          {
            element: <MainLayout />,
            children: [
              {
                path: LocationUrls.PLAYER,
                lazy: async () => {
                  // Preload HomeScreen while the player is loading. A failed
                  // preload is swallowed on purpose: nothing is waiting on it,
                  // and letting it reject would raise an unhandled chunk error
                  // that reloads the app out from under a working player.
                  // Navigating to Home re-imports the chunk, and that failure
                  // is the one the root error boundary should see.
                  loadHomeScreen().catch((): void => undefined);
                  const { default: FullScreenPlayback } =
                    await import("../VideoPlayerUI/FullScreenPlayback");
                  return { Component: FullScreenPlayback };
                }
              },
              {
                element: <NavLayout />,
                children: [
                  {
                    path: LocationUrls.SELECT_PROFILE,
                    lazy: async () => {
                      const { default: SelectProfileScreen } =
                        await import("../Profiles/SelectProfileScreen");
                      return { Component: SelectProfileScreen };
                    }
                  },
                  {
                    path: LocationUrls.HOME_SCREEN,
                    lazy: async () => {
                      const { default: HomeScreen } = await loadHomeScreen();
                      return { Component: HomeScreen };
                    }
                  },
                  {
                    path: LocationUrls.GUIDE,
                    lazy: async () => {
                      const { default: GuideScreen } =
                        await import("../Guide/GuideScreen");
                      return { Component: GuideScreen };
                    }
                  },
                  {
                    path: LocationUrls.MY_HUB,
                    lazy: async () => {
                      const { default: MyHubScreen } =
                        await import("../MyHub/MyHubScreen");
                      return { Component: MyHubScreen };
                    }
                  },
                  {
                    path: LocationUrls.MY_HUB_MANAGE_SCREEN,
                    lazy: async () => {
                      const { default: ManageSeriesRecordingsRoute } =
                        await import("../MyHub/ManageSeriesRecordings/ManageSeriesRecordingsRoute");
                      return { Component: ManageSeriesRecordingsRoute };
                    }
                  },
                  {
                    path: LocationUrls.MY_HUB_SCHEDULED_SERIES,
                    lazy: async () => {
                      const { default: ScheduledSeriesRoute } =
                        await import("../MyHub/ScheduledRecordings/ScheduledSeriesRoute");
                      return { Component: ScheduledSeriesRoute };
                    }
                  },
                  {
                    path: LocationUrls.MY_HUB_SCHEDULED_SINGLE,
                    lazy: async () => {
                      const { default: ScheduledSingleRoute } =
                        await import("../MyHub/ScheduledRecordings/ScheduledSingleRoute");
                      return { Component: ScheduledSingleRoute };
                    }
                  },
                  {
                    path: LocationUrls.SHOWS_AND_MOVIES,
                    lazy: async () => {
                      const { default: ShowsAndMoviesScreen } =
                        await import("../ShowsAndMovies/ShowsAndMoviesScreen");
                      return { Component: ShowsAndMoviesScreen };
                    }
                  },
                  {
                    path: LocationUrls.CONTENT_DETAIL,
                    lazy: async () => {
                      const { ContentDetailScreen } =
                        await import("../ContentDetailScreen");
                      return { Component: ContentDetailScreen };
                    }
                  },
                  {
                    path: LocationUrls.SEARCH,
                    lazy: async () => {
                      const { default: SearchScreen } =
                        await import("../Search/SearchScreen");
                      return { Component: SearchScreen };
                    }
                  },
                  {
                    path: LocationUrls.SEE_MORE,
                    lazy: async () => {
                      const { default: SeeMoreScreen } =
                        await import("../SeeMore/SeeMoreScreen");
                      return { Component: SeeMoreScreen };
                    }
                  },
                  {
                    path: LocationUrls.SETTINGS,
                    lazy: async () => {
                      const { default: SettingsScreen } =
                        await import("../Settings/SettingsScreen");
                      return { Component: SettingsScreen };
                    }
                  },
                  {
                    path: LocationUrls.PROFILES,
                    lazy: async () => {
                      const { default: ManageProfilesScreen } =
                        await import("../Profiles/ManageProfilesScreen");
                      return { Component: ManageProfilesScreen };
                    }
                  },
                  {
                    path: LocationUrls.PROFILE_DETAILS,
                    lazy: async () => {
                      const { default: ProfileDetailsScreen } =
                        await import("../Profiles/ProfileDetailsScreen");
                      return { Component: ProfileDetailsScreen };
                    }
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  }
]);
